import { Button, Card, useToast } from '@citizen-walk/shared-ui';
import { isSafeHttpUrl, type District, type MapDownloadPolicy } from '@citizen-walk/shared-types';
import { useState } from 'react';
import { doc, deleteField, serverTimestamp, setDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';

/**
 * 地区その他設定 panel (Sprint BG-DataSettings 15、旧 v1 misc fields 編集セクション踏襲)。
 *
 * 旧 v1 `old/apps/admin/src/pages/DistrictDetailPage.tsx`:
 *   - line 519〜566: installPromptEnabled / analysisSiteUrl / surveyUrl 編集
 *   - line 638〜643: allowPostingWithoutSession 編集
 *   - line 204〜207: mapDownloadPolicy 編集 (manual/auto)
 *
 * 踏襲した点:
 *   - 5 field (installPromptEnabled / surveyUrl / allowPostingWithoutSession / analysisSiteUrl /
 *     mapDownloadPolicy) を 1 panel に集約
 *   - 旧の default 値: installPromptEnabled=true / allowPostingWithoutSession=false /
 *     mapDownloadPolicy='manual'
 *   - URL 系 (surveyUrl / analysisSiteUrl) は空文字 → null 正規化 (旧 line 399-400 と同)
 *
 * 変えた点 (v2):
 *   - mapDownloadPolicy は本来 District の正式 field、surveyUrl / installPromptEnabled /
 *     allowPostingWithoutSession は v2 shared-types 未追加のため `as unknown` cast で書込
 *     (forward-compatible、後続 sprint で型化予定)
 *   - 旧の inline placeholder hint / 単位ヘルプ文を整理
 */

export interface DistrictMiscDraft {
  installPromptEnabled: boolean;
  surveyUrl: string;
  allowPostingWithoutSession: boolean;
  analysisSiteUrl: string;
  mapDownloadPolicy: MapDownloadPolicy;
}

/** pure: District → draft (型外 field は unknown 経由で read)。 */
export function readMiscFromDistrict(d: District): DistrictMiscDraft {
  const extra = d as unknown as Record<string, unknown>;
  const installPromptEnabled =
    typeof extra.installPromptEnabled === 'boolean' ? extra.installPromptEnabled : true;
  const allowPostingWithoutSession =
    typeof extra.allowPostingWithoutSession === 'boolean'
      ? extra.allowPostingWithoutSession
      : false;
  const surveyUrl = typeof extra.surveyUrl === 'string' ? extra.surveyUrl : '';
  return {
    installPromptEnabled,
    allowPostingWithoutSession,
    surveyUrl,
    analysisSiteUrl: d.analysisSiteUrl ?? '',
    mapDownloadPolicy: d.mapDownloadPolicy ?? 'manual',
  };
}

/**
 * pure: 空文字を null に、空 URL field は deleteField sentinel (テストでは null を期待)。
 * http(s) 以外 (javascript: 等) も null にする (docs/13 §7)。保存前に `findInvalidMiscUrl` で
 * 弾いて利用者に知らせるので、ここは保存経路の最後の安全網。
 */
export function normalizeMiscUrl(v: string): string | null {
  const t = v.trim();
  if (t.length === 0) return null;
  return isSafeHttpUrl(t) ? t : null;
}

/**
 * pure: 入力済みなのに http(s) の URL として使えない項目の名前を返す (全部正しければ null)。
 * 画面に出す文言なので内部の項目名ではなく日本語の名前を返す。
 */
export function findInvalidMiscUrl(
  draft: Pick<DistrictMiscDraft, 'surveyUrl' | 'analysisSiteUrl'>,
): string | null {
  const entries: Array<[string, string]> = [
    ['外部アンケート URL', draft.surveyUrl],
    ['外部分析サイト URL', draft.analysisSiteUrl],
  ];
  for (const [label, value] of entries) {
    const t = value.trim();
    if (t.length > 0 && !isSafeHttpUrl(t)) return label;
  }
  return null;
}

/** pure: mapDownloadPolicy が valid な値か。 */
export function isValidMapDownloadPolicy(v: unknown): v is MapDownloadPolicy {
  return v === 'manual' || v === 'auto';
}

/** pure: draft → firestore payload (URL は normalize、bool はそのまま)。 */
export function buildMiscPayload(draft: DistrictMiscDraft) {
  return {
    installPromptEnabled: !!draft.installPromptEnabled,
    allowPostingWithoutSession: !!draft.allowPostingWithoutSession,
    surveyUrl: normalizeMiscUrl(draft.surveyUrl),
    analysisSiteUrl: normalizeMiscUrl(draft.analysisSiteUrl),
    mapDownloadPolicy: isValidMapDownloadPolicy(draft.mapDownloadPolicy)
      ? draft.mapDownloadPolicy
      : 'manual',
  };
}

export interface DistrictMiscPanelProps {
  district: District;
  uid: string;
  canEdit: boolean;
}

export function DistrictMiscPanel({ district, uid, canEdit }: DistrictMiscPanelProps) {
  const { toast } = useToast();
  const [draft, setDraft] = useState<DistrictMiscDraft>(() => readMiscFromDistrict(district));
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (!canEdit) return;
    const invalid = findInvalidMiscUrl(draft);
    if (invalid) {
      toast('error', `${invalid}は https:// または http:// で始まる URL を入力してください`);
      return;
    }
    setSaving(true);
    try {
      const payload = buildMiscPayload(draft);
      // Firestore で null を書込むと field 削除ではなく null 値が入る (どちらでも user 側 fallback 動作可)。
      // 旧 v1 でも null セットだったので踏襲。
      const writePayload: Record<string, unknown> = {
        installPromptEnabled: payload.installPromptEnabled,
        allowPostingWithoutSession: payload.allowPostingWithoutSession,
        surveyUrl: payload.surveyUrl,
        // analysisSiteUrl は District 正式 field のため、null なら deleteField で field 削除も可だが
        // 旧と同じく null を入れる (consumer 側で ?? で fallback している前提)。
        analysisSiteUrl: payload.analysisSiteUrl ?? deleteField(),
        mapDownloadPolicy: payload.mapDownloadPolicy,
        updatedAt: serverTimestamp(),
        updatedBy: uid,
      };
      const ref = doc(db, 'districts', district.id);
      await setDoc(ref, writePayload, { merge: true });
      toast('success', 'その他設定を保存しました');
    } catch (e) {
      toast('error', `保存失敗: ${(e as Error).message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card padding="lg" className="border-primary-200" data-testid="district-misc-panel">
      <h3 className="text-base font-bold text-slate-900 mb-3">⚙️ その他設定</h3>
      <p className="text-xs text-slate-500 mb-3">
        アプリ導入の案内・外部アンケート・地図ダウンロード方法などの細かい設定。
      </p>

      <div className="grid grid-cols-1 gap-3">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={draft.installPromptEnabled}
            onChange={(e) => setDraft({ ...draft, installPromptEnabled: e.target.checked })}
            disabled={!canEdit || saving}
            className="h-4 w-4"
            data-testid="misc-install-prompt"
          />
          <span className="text-sm">PWA「ホームに追加」バナーを表示する</span>
        </label>

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={draft.allowPostingWithoutSession}
            onChange={(e) => setDraft({ ...draft, allowPostingWithoutSession: e.target.checked })}
            disabled={!canEdit || saving}
            className="h-4 w-4"
            data-testid="misc-allow-no-session"
          />
          <span className="text-sm">セッション未参加でも投稿可能にする</span>
        </label>

        <label className="grid grid-cols-1 gap-1">
          <span className="text-xs text-slate-500">外部アンケート URL（任意）</span>
          <input
            type="url"
            value={draft.surveyUrl}
            onChange={(e) => setDraft({ ...draft, surveyUrl: e.target.value })}
            disabled={!canEdit || saving}
            placeholder="https://forms.example.com/..."
            className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
            data-testid="misc-survey-url"
          />
        </label>

        <label className="grid grid-cols-1 gap-1">
          <span className="text-xs text-slate-500">
            外部分析サイト URL（任意・外部の分析サイト）
          </span>
          <input
            type="url"
            value={draft.analysisSiteUrl}
            onChange={(e) => setDraft({ ...draft, analysisSiteUrl: e.target.value })}
            disabled={!canEdit || saving}
            placeholder="https://analysis.example.com/..."
            className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
            data-testid="misc-analysis-url"
          />
        </label>

        <label className="grid grid-cols-1 gap-1">
          <span className="text-xs text-slate-500">オフライン地図のダウンロード方法</span>
          <select
            value={draft.mapDownloadPolicy}
            onChange={(e) =>
              setDraft({
                ...draft,
                mapDownloadPolicy: e.target.value as MapDownloadPolicy,
              })
            }
            disabled={!canEdit || saving}
            className="min-h-touch px-3 border border-slate-300 rounded text-sm bg-white"
            data-testid="misc-map-policy"
          >
            <option value="manual">手動（参加者が自分でダウンロード）</option>
            <option value="auto">自動（参加時にダウンロードを案内）</option>
          </select>
        </label>
      </div>

      <div className="flex justify-end mt-3">
        <Button
          onClick={() => void handleSave()}
          disabled={!canEdit || saving}
          data-testid="misc-save"
        >
          {saving ? '保存中…' : '保存'}
        </Button>
      </div>
    </Card>
  );
}
